import { Button, Icon, IconButton } from '@blend-ed/blendx-ui';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { sendMessage } from "../../api/AIAssistant";
import useFetchAiChatHistory from "../../hooks/aiChat/useFetchAiChatHistory";
import useFetchAiChatSessions from "../../hooks/aiChat/useFetchAiChatSessions";
import LoadingIndicator from './ai-home/components/LoadingIndicator';
import MessageGroup from './ai-home/components/MessageGroup';
import { CHAT_SUGGESTIONS } from './data/suggestions';
import messages from './messages';

// Initial Suggestions Component if no messages are present
function SuggestionsView({ onSuggestionClick, intl, availableSuggestions }) {
  return (
    <div className="suggestions-container">
      <h2>{intl.formatMessage(messages.howCanIHelp)}</h2>
      <div className="suggestions-grid">
        {availableSuggestions.map((suggestion, index) => (
          <Button
            key={index}
            variant="outline"
            className="suggestion-button"
            onClick={() => onSuggestionClick(intl.formatMessage(suggestion.action), suggestion.id)}
            leftIcon={<Icon icon={suggestion.icon} type="line" />}
          >
            {intl.formatMessage(suggestion.text)}
          </Button>
        ))}
      </div>
    </div>
  );
}

// Chat Input Component
function ChatInput({ value, onChange, onSend, intl }) {
  const [showCommands, setShowCommands] = useState(false);
  const [commandFilter, setCommandFilter] = useState('');
  const inputRef = useRef(null);

  const commands = [
    { id: 'course', name: 'Course Search', description: 'Search in course content and generate learning pathway', icon: 'book' },
    { id: 'kb', name: 'Knowledge Base', description: 'Search in knowledge base', icon: 'book-shelf' }
  ];

  const filteredCommands = commands.filter(cmd =>
    cmd.name.toLowerCase().includes(commandFilter.toLowerCase()) ||
    cmd.description.toLowerCase().includes(commandFilter.toLowerCase())
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Check if the input starts with a slash and has no space after the command
    if (newValue.startsWith('/') && !newValue.includes(' ')) {
      setShowCommands(true);
      setCommandFilter(newValue.slice(1));
    } else {
      setShowCommands(false);
    }
  };

  const handleCommandClick = (command) => {
    onChange(`/${command.id} `);
    setShowCommands(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showCommands && filteredCommands.length > 0) {
        // If commands are showing and we have at least one command, select the first one
        handleCommandClick(filteredCommands[0]);
      } else if (!showCommands) {
        // If no commands are showing, send the message
        onSend();
      }
    } else if (e.key === 'Escape' && showCommands) {
      setShowCommands(false);
    }
  };

  return (
    <div className="input-area">
      <div className="input-container">
        <div className="input-wrapper">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage(messages.askAnything)}
          />
        </div>
        {showCommands && (
          <div className="command-suggestions">
            {filteredCommands.map((cmd) => (
              <button
                key={cmd.id}
                className="command-item"
                onClick={() => handleCommandClick(cmd)}
              >
                <div className="command-name">
                  <Icon icon={cmd.icon} type="line" className="command-icon" size="sm" /> {cmd.name}
                </div>
                <div className="command-description">{cmd.description}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <IconButton
        onClick={onSend}
        icon="send-plane"
        iconType="line"
        disabled={!value.trim()}
      />
    </div>
  );
}

// Main Chat Component
function ChatView({ messages, inputText, setInputText, handleSendMessage, messageListRef, intl, onExpandView, initialAvailableSuggestions, isLoading, handleLearnerLearningPath }) {
  const [showScrollButton, setShowScrollButton] = useState(false);

  const handleScroll = () => {
    if (messageListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messageListRef.current;
      // Show button when scrolled up more than 200px from bottom
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 200);
    }
  };

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  };

  return (
    <div className="chat-container">
      <div
        className="message-list"
        ref={messageListRef}
        onScroll={handleScroll}
      >
        {messages.length === 0 ? (
          <SuggestionsView onSuggestionClick={handleSendMessage} intl={intl} availableSuggestions={initialAvailableSuggestions} />
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageGroup
                key={message.id}
                message={message}
                onSuggestionClick={handleSendMessage}
                onExpandView={onExpandView}
                intl={intl}
                isLastMessage={index === messages.length - 1}
                handleLearnerLearningPath={handleLearnerLearningPath}
              />
            ))}
            {isLoading && <LoadingIndicator />}
          </>
        )}
      </div>
      <div className="chat-bottom-controls">
        {showScrollButton && (
          <IconButton
            className="scroll-bottom-button"
            icon="arrow-down"
            iconType="line"
            variant="ghost"
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
          />
        )}
        <ChatInput
          value={inputText}
          onChange={setInputText}
          onSend={() => handleSendMessage()}
          intl={intl}
        />
      </div>
    </div>
  );
}

// Main Component
function AiHome({ intl, setExpandedView, handleLearnerLearningPath }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messageListRef = useRef(null);
  const { authenticatedUser } = useContext(AppContext);

  const { data: chatSessionsData, loading: chatSessionsLoading, error: chatSessionsError } = useFetchAiChatSessions(authenticatedUser.id, 'learner');

  const chatSessions = useMemo(() => {
    if (!chatSessionsData || chatSessionsLoading) {
      return [];
    }

    return chatSessionsData?.map(session => ({
      id: session.id,
      name: session.sessionName
    }));
  }, [chatSessionsData]);

  const { data: chatHistory, loading, error } = useFetchAiChatHistory(authenticatedUser.id, chatSessions[0]?.id);

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Scroll to bottom when chat history is loaded
    if (chatHistory?.length > 0) {
      setTimeout(scrollToBottom, 100); // Small delay to ensure DOM is updated
    }
  }, [chatHistory]);

  // Send Message to AI bot and get response
  const sendMessageMutation = useReactQueryMutation({
    mutationFn: (message) => sendMessage(message),
    onSuccess: (data) => {
      setIsLoading(false);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'ai',
        content: data.message,
        contentType: 'text',
        createdAt: new Date().toISOString(),
      }]);
      if (data.generated_learning_path && data.generated_learning_path.length > 0) {
        const firstPathItem = data.generated_learning_path[0];
        setMessages(prev => [...prev,
        {
          id: Date.now(),
          type: 'ai',
          content: {
            courseId: firstPathItem.course_name,
            blockId: firstPathItem.unit_id,
            title: firstPathItem.unit,
          },
          generatedLearningPath: data.generated_learning_path,
          contentType: 'xblock',
          createdAt: new Date().toISOString(),

        }
        ]);
      }
    }
  });

  // Fetch Ai Chat History
  useEffect(() => {
    if (chatHistory?.length > 0) {
      const reverseChatHistory = [...chatHistory].reverse();
      let structuredMessages = [];

      reverseChatHistory.forEach(chat => {
        // Add the main message object
        structuredMessages.push({
          id: chat.id,
          type: chat.role === "user" ? "user" : "ai",
          content: chat.message,
          contentType: 'text',
          createdAt: chat.createdAt,
        });

        // If generatedLearningPath exists and is not empty, add the first item as a separate message
        if (chat.generatedLearningPath && chat.generatedLearningPath.length > 0 && chat.role !== "user") {
          const firstPathItem = chat.generatedLearningPath[0];
          structuredMessages.push({
            id: `${chat.id}-path`,
            type: "ai",
            content: {
              courseId: firstPathItem.course_name,
              blockId: firstPathItem.unit_id,
              title: firstPathItem.unit,
            },
            generatedLearningPath: chat.generatedLearningPath,
            contentType: 'xblock',
            createdAt: chat.createdAt,
          });
        }
      });

      setMessages(structuredMessages);
    }
  }, [chatHistory]);

  const initialAvailableSuggestions = useMemo(() => {
    return CHAT_SUGGESTIONS.filter(suggestion => {
      if (suggestion.id === 'continue-course') {
        return false;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    if (messages?.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const handleSendMessage = (text = inputText, suggestionId = null) => {
    if (!text.trim()) return;

    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: text,
      contentType: 'text',
      createdAt: new Date().toISOString(),
      isNewMessage: true
    }]);
    setInputText('');

    setIsLoading(true);
    sendMessageMutation.mutate(text);
  };

  return (
    <div className="learning-canvas">
      <ChatView
        messages={messages}
        inputText={inputText}
        setInputText={setInputText}
        handleSendMessage={handleSendMessage}
        messageListRef={messageListRef}
        intl={intl}
        onExpandView={setExpandedView}
        initialAvailableSuggestions={initialAvailableSuggestions}
        isLoading={isLoading}
        handleLearnerLearningPath={handleLearnerLearningPath}
      />
    </div>
  );
}

AiHome.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(AiHome); 